<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { onDestroy, onMount } from 'svelte';
  import PhDiagram from '#lib/refrigerant/PhDiagram.svelte';
  import { trackToolUse } from '#lib/analytics.ts';
  import {
    calculateRefrigerant,
    fetchDiagram,
    fetchFluids,
    type CalculateResponse,
    type DiagramResponse,
    type EnthalpyUnit,
    type PressureUnit,
    type TemperatureUnit,
  } from '#lib/refrigerant/api.ts';

  const STORAGE_KEY = 'spinflip_refrigerant_prefs';
  const AUTO_CALC_DEBOUNCE_MS = 800;

  let fluids: string[] = ['R410A', 'R134a', 'R22'];
  let fluid = 'R410A';

  let pressureUnit: PressureUnit = 'psig';
  let temperatureUnit: TemperatureUnit = 'F';
  let enthalpyUnit: EnthalpyUnit = 'kJ_kg';
  let atmosphericKpa = 101.325;

  let lowPressure = 118;
  let lowTemperature = 45;
  let highPressure = 400;
  let highTemperature = 95;

  let diagram: DiagramResponse | null = null;
  let result: CalculateResponse | null = null;
  let error = '';
  let loading = false;
  let autoCalcEnabled = false;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let calcInFlight = false;
  let pendingAutoCalc = false;
  let calcGeneration = 0;

  function inputsReady(): boolean {
    return [lowPressure, lowTemperature, highPressure, highTemperature, atmosphericKpa].every(
      (v) => typeof v === 'number' && Number.isFinite(v),
    );
  }

  function loadPrefs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const prefs = JSON.parse(raw) as Record<string, string>;
      if (prefs.pressureUnit) pressureUnit = prefs.pressureUnit as PressureUnit;
      if (prefs.temperatureUnit) temperatureUnit = prefs.temperatureUnit as TemperatureUnit;
      if (prefs.enthalpyUnit) enthalpyUnit = prefs.enthalpyUnit as EnthalpyUnit;
      if (prefs.fluid) fluid = prefs.fluid;
    } catch {
      // ignore
    }
  }

  function savePrefs() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ pressureUnit, temperatureUnit, enthalpyUnit, fluid }),
      );
    } catch {
      // ignore
    }
  }

  async function loadDiagram() {
    diagram = await fetchDiagram({
      fluid,
      pressure_unit: pressureUnit,
      enthalpy_unit: enthalpyUnit,
      atmospheric_kpa: atmosphericKpa,
    });
  }

  async function runCalculate() {
    if (calcInFlight) {
      pendingAutoCalc = true;
      return;
    }
    if (!inputsReady()) return;

    const gen = ++calcGeneration;
    calcInFlight = true;
    error = '';
    loading = true;
    try {
      savePrefs();
      const newDiagram = await fetchDiagram({
        fluid,
        pressure_unit: pressureUnit,
        enthalpy_unit: enthalpyUnit,
        atmospheric_kpa: atmosphericKpa,
      });
      if (gen !== calcGeneration) return;
      diagram = newDiagram;

      const newResult = await calculateRefrigerant({
        fluid,
        low: {
          pressure: lowPressure,
          pressure_unit: pressureUnit,
          temperature: lowTemperature,
          temperature_unit: temperatureUnit,
        },
        high: {
          pressure: highPressure,
          pressure_unit: pressureUnit,
          temperature: highTemperature,
          temperature_unit: temperatureUnit,
        },
        atmospheric_kpa: atmosphericKpa,
        enthalpy_unit: enthalpyUnit,
        pressure_unit: pressureUnit,
        temperature_unit: temperatureUnit,
      });
      if (gen !== calcGeneration) return;
      result = newResult;
      autoCalcEnabled = true;
    } catch (e) {
      if (gen !== calcGeneration) return;
      result = null;
      error = e instanceof Error ? e.message : 'Calculation failed';
    } finally {
      if (gen === calcGeneration) {
        calcInFlight = false;
        loading = false;
      }
      const pending = pendingAutoCalc;
      pendingAutoCalc = false;
      if (pending) scheduleAutoCalc();
    }
  }

  /** Only explicit clicks are tracked; auto-recalcs on every input would flood GA. */
  function onCalculateClick() {
    trackToolUse('refrigerant', 'calculate', { fluid });
    runCalculate();
  }

  function scheduleAutoCalc() {
    if (!autoCalcEnabled) return;
    if (!inputsReady()) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      runCalculate();
    }, AUTO_CALC_DEBOUNCE_MS);
  }

  function onInputChange() {
    scheduleAutoCalc();
  }

  async function onFluidChange() {
    diagram = null;
    result = null;
    if (autoCalcEnabled) {
      scheduleAutoCalc();
    } else {
      try {
        await loadDiagram();
      } catch {
        // ignore until calculate
      }
    }
  }

  async function onUnitChange() {
    if (autoCalcEnabled) {
      scheduleAutoCalc();
      return;
    }
    try {
      await loadDiagram();
    } catch {
      // ignore
    }
  }

  onDestroy(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
  });

  onMount(async () => {
    loadPrefs();
    try {
      fluids = await fetchFluids();
      if (!fluids.includes(fluid)) fluid = fluids[0] ?? 'R410A';
      await loadDiagram();
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not load refrigerant data';
    }
  });

  $: tempSuffix = temperatureUnit === 'F' ? '°F' : '°C';
  $: enthalpySuffix = enthalpyUnit === 'kJ_kg' ? 'kJ/kg' : 'Btu/lb';
</script>

<svelte:head>
  <title>{pageTitle('Refrigerant Calculator')}</title>
</svelte:head>

<div class="refrigerant-page">
  <section class="py-5 bg-light page-hero">
    <div class="container">
      <div class="row mb-4">
        <div class="col-lg-12 text-center">
          <h1 class="section-heading text-uppercase">Refrigerant Calculator</h1>
          <p class="section-subheading text-muted">
            Superheat, subcooling, and pressure–enthalpy (P–h) diagram for field measurements.
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="py-4">
    <div class="container">
      {#if error}
        <div class="alert alert-danger" role="alert">{error}</div>
      {/if}

      <div class="row g-4">
        <div class="col-lg-5">
          <div class="card shadow-sm">
            <div class="card-body">
              <h2 class="h5 text-uppercase mb-3">Inputs</h2>

              <div class="mb-3">
                <label class="form-label" for="fluid">Refrigerant</label>
                <select
                  id="fluid"
                  class="form-control"
                  bind:value={fluid}
                  on:change={onFluidChange}
                >
                  {#each fluids as f}
                    <option value={f}>{f}</option>
                  {/each}
                </select>
              </div>

              <div class="row g-2 mb-3">
                <div class="col-4">
                  <label class="form-label" for="pressureUnit">Pressure</label>
                  <select
                    id="pressureUnit"
                    class="form-control"
                    bind:value={pressureUnit}
                    on:change={onUnitChange}
                  >
                    <option value="psig">psig</option>
                    <option value="psia">psia</option>
                    <option value="kPa">kPa</option>
                    <option value="bar">bar</option>
                  </select>
                </div>
                <div class="col-4">
                  <label class="form-label" for="temperatureUnit">Temperature</label>
                  <select
                    id="temperatureUnit"
                    class="form-control"
                    bind:value={temperatureUnit}
                    on:change={onUnitChange}
                  >
                    <option value="F">°F</option>
                    <option value="C">°C</option>
                  </select>
                </div>
                <div class="col-4">
                  <label class="form-label" for="enthalpyUnit">Enthalpy</label>
                  <select
                    id="enthalpyUnit"
                    class="form-control"
                    bind:value={enthalpyUnit}
                    on:change={onUnitChange}
                  >
                    <option value="kJ_kg">kJ/kg</option>
                    <option value="btu_lb">Btu/lb</option>
                  </select>
                </div>
              </div>

              <p class="small text-muted">
                Gauge pressures (psig, kPa, bar) use atmospheric {atmosphericKpa} kPa unless
                changed below.
              </p>
              <div class="mb-3">
                <label class="form-label" for="atm">Atmospheric pressure (kPa)</label>
                <input
                  id="atm"
                  type="number"
                  class="form-control"
                  step="0.1"
                  min="80"
                  max="110"
                  bind:value={atmosphericKpa}
                  on:input={onInputChange}
                />
              </div>

              <hr />

              <h3 class="h6 text-uppercase">Low side (suction / evaporator)</h3>
              <div class="row g-2 mb-3">
                <div class="col-6">
                  <label class="form-label" for="lowP">Pressure ({pressureUnit})</label>
                  <input
                    id="lowP"
                    type="number"
                    class="form-control"
                    step="any"
                    bind:value={lowPressure}
                    on:input={onInputChange}
                  />
                </div>
                <div class="col-6">
                  <label class="form-label" for="lowT">Line temp ({tempSuffix})</label>
                  <input
                    id="lowT"
                    type="number"
                    class="form-control"
                    step="any"
                    bind:value={lowTemperature}
                    on:input={onInputChange}
                  />
                </div>
              </div>

              <h3 class="h6 text-uppercase">High side (liquid / condenser)</h3>
              <div class="row g-2 mb-3">
                <div class="col-6">
                  <label class="form-label" for="highP">Pressure ({pressureUnit})</label>
                  <input
                    id="highP"
                    type="number"
                    class="form-control"
                    step="any"
                    bind:value={highPressure}
                    on:input={onInputChange}
                  />
                </div>
                <div class="col-6">
                  <label class="form-label" for="highT">Line temp ({tempSuffix})</label>
                  <input
                    id="highT"
                    type="number"
                    class="form-control"
                    step="any"
                    bind:value={highTemperature}
                    on:input={onInputChange}
                  />
                </div>
              </div>

              <button
                type="button"
                class="btn btn-primary btn-lg w-100"
                disabled={loading}
                on:click={onCalculateClick}
              >
                {loading ? 'Calculating…' : 'Calculate'}
              </button>
            </div>
          </div>
        </div>

        <div class="col-lg-7">
          {#if result}
            {#if result.warnings.length}
              <div class="alert alert-warning">
                {#each result.warnings as w}
                  <div>{w}</div>
                {/each}
              </div>
            {/if}

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="card shadow-sm h-100 border-primary">
                  <div class="card-body">
                    <h3 class="h6 text-uppercase text-muted">Superheat</h3>
                    <p class="display-6 mb-0">
                      {result.low.superheat_display?.toFixed(1) ?? '—'}
                      <small>{tempSuffix}</small>
                    </p>
                    <p class="small text-muted mb-0 mt-2">
                      Saturation at suction: {result.low.t_sat_display.toFixed(1)}{tempSuffix}
                    </p>
                    {#if result.low.note}
                      <p class="small text-warning mb-0">{result.low.note}</p>
                    {/if}
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card shadow-sm h-100 border-danger">
                  <div class="card-body">
                    <h3 class="h6 text-uppercase text-muted">Subcooling</h3>
                    <p class="display-6 mb-0">
                      {result.high.subcooling_display?.toFixed(1) ?? '—'}
                      <small>{tempSuffix}</small>
                    </p>
                    <p class="small text-muted mb-0 mt-2">
                      Saturation at liquid: {result.high.t_sat_display.toFixed(1)}{tempSuffix}
                    </p>
                    {#if result.high.note}
                      <p class="small text-warning mb-0">{result.high.note}</p>
                    {/if}
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-3">
              <div class="card-body py-2">
                <div class="row small">
                  <div class="col-sm-6">
                    <strong>Low:</strong>
                    {result.low.t_display.toFixed(1)}{tempSuffix},
                    h = {result.low.h_display.toFixed(1)} {enthalpySuffix}
                  </div>
                  <div class="col-sm-6">
                    <strong>High:</strong>
                    {result.high.t_display.toFixed(1)}{tempSuffix},
                    h = {result.high.h_display.toFixed(1)} {enthalpySuffix}
                  </div>
                </div>
              </div>
            </div>

          {:else}
            <p class="text-muted">
              Enter suction and liquid-line readings, then click Calculate to see superheat,
              subcooling, and state points on the P–h diagram.
            </p>
          {/if}

          <div class="card shadow-sm">
            <div class="card-body">
              <h2 class="h6 text-uppercase mb-2">Pressure–enthalpy diagram</h2>
              {#if diagram}
                <PhDiagram
                  {diagram}
                  {result}
                  {pressureUnit}
                  {enthalpyUnit}
                  {temperatureUnit}
                />
              {:else}
                <p class="text-muted mb-0">Loading diagram…</p>
              {/if}
            </div>
          </div>

          <p class="small text-muted mt-3 mb-0">
            Superheat = suction line temperature minus saturation temperature at suction
            pressure. Subcooling = saturation temperature at liquid pressure minus liquid line
            temperature. Dashed lines on the diagram connect saturation to measured state at the
            same pressure; diamond markers sit on the vapor (suction) or liquid (discharge) dome.
            Bubble-point saturation is used for temperature deltas on blends (e.g. R410A).
          </p>
        </div>
      </div>
    </div>
  </section>
</div>

<style>
  .page-hero {
    border-bottom: 1px solid #dee2e6;
  }

  .refrigerant-page :global(.display-6) {
    font-size: 2.25rem;
    font-weight: 600;
  }
</style>
